/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './headspace-overview.scss';

/* eslint-disable import/no-unresolved, import/default */

import pageTemplate from './headspace-overview.tpl.html';
import {displayTelemetryTooltip} from "../../../../../utils/tooltip-utility";

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.headSpaceOverview', [])
    .directive('agHeadSpaceOverview', HeadSpaceOverview)
    .name;

/*@ngInject*/
function HeadSpaceOverview() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            isDashboard: '=',
            providedAssetId: '='
        },
        controller: HeadSpaceController,
        controllerAs: 'vm',
        templateUrl: pageTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function HeadSpaceController($scope, $filter, $translate, $q, $log, $compile, types, agTypes, agUtils, siloService, unitService, userService) {

    let vm = this;

    vm.agTypes = agTypes;
    vm.loading = false;
    vm.temperatureUnits = unitService.temperatureUnitSymbol();
    vm.co2LabelMargin = 0;
    vm.resolveCo2Color = resolveCo2Color;
    vm.resolveDewDiffColor = resolveDewDiffColor;
    vm.displayValueTooltip = displayValueTooltip;
    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: () => {}
        },
        useDashboardTimewindow: false
    };

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.siloId = null;

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };

    $scope.$watch('vm.ctx', function() {
        if (vm.ctx && vm.showData) {
            vm.ctx.widgetActions = [];
            vm.ctx.hideTitlePanel = false;
            vm.siloId = null;
            vm.silo = null;
            vm.headSpaceSensor = null;

            if (vm.providedAssetId) {
                vm.siloId = vm.providedAssetId;
            } else {
                if (vm.ctx.stateController) {
                    vm.siloId = vm.ctx.stateController.getStateParams().entityId;
                }

                $scope.$watch(function () {
                        return vm.ctx.stateController.getStateParams().entityId;
                    },
                    function (newSiloId, prevSiloId) {
                        if (newSiloId && !angular.equals(newSiloId, prevSiloId)) {
                            vm.siloId = newSiloId;
                            loadSiloInformation();
                        }
                    }
                );
            }

            if (vm.siloId) {
                loadSiloInformation();
            }
        }
    });

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.tempVal = null;
        vm.dewpVal = null;
        vm.diff = null; // tempVal - dewpVal
        vm.co2Val = null;
        vm.pressVal = null;
        vm.humidityVal = null;

        vm.tempTs = null;
        vm.dewpTs = null;
        vm.co2Ts = null;
        vm.pressTs = null;
        vm.humidityTs = null;

        vm.co2ValLabel = '';
        vm.tempValLabel = '';
        vm.dewpValLabel = '';
        vm.pressValLabel = '';
        vm.diffLabel = '';
        vm.humidityValLabel = '';
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        let calculateDewpointCanvas = false;
        for (var i = 0; i < data.length; i++) {
            var datasourceData = data[i];
            var val = getNumberValue(datasourceData);
            var ts = getFormattedTs(datasourceData);

            switch (datasourceData.dataKey.name) {
                case vm.agTypes.telemetry.headSpaceSensor.temperature:
                    vm.tempVal = val;
                    vm.tempTs = ts;
                    vm.tempValLabel = vm.ctx.utils.formatValue(val, 1, '');
                    calculateDewpointCanvas = true;
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.dewpoint:
                    vm.dewpVal = val;
                    vm.dewpTs = ts;
                    vm.dewpValLabel = vm.ctx.utils.formatValue(val, 1, '');
                    calculateDewpointCanvas = true;
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.co2level:
                    vm.co2Val = val;
                    vm.co2Ts = ts;
                    vm.co2ValLabel = vm.ctx.utils.formatValue(val, 1, agTypes.units.co2Level);
                    calculateCo2CanvasData()
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.humidity:
                    vm.humidityVal = val;
                    vm.humidityTs = ts;
                    vm.humidityValLabel = vm.ctx.utils.formatValue(val, 1, agTypes.units.humidity + 'RH');
                    break;
                case vm.agTypes.telemetry.headSpaceSensor.pressure:
                    vm.pressTs = ts;
                    vm.pressVal = val;
                    vm.pressValLabel = vm.ctx.utils.formatValue(val, 1, agTypes.units.pressure);
                    break;
            }
        }

        if (vm.tempVal !== null && vm.dewpVal !== null) {
            vm.diff = vm.tempVal - vm.dewpVal;
            vm.diffLabel = vm.ctx.utils.formatValue(vm.diff, 1, '');

            if (calculateDewpointCanvas) {
                calculateDewpointCanvasData();
            }
        }

        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function calculateCo2CanvasData() {
        const co2 = vm.co2Val !== null ? vm.co2Val : 0;
        const minV = Math.min(0, co2, vm.silo.maxHeadSpaceCo2WarningThreshold, vm.silo.maxHeadSpaceCo2CriticalThreshold);
        const maxV = Math.max(co2, vm.silo.maxHeadSpaceCo2WarningThreshold, vm.silo.maxHeadSpaceCo2CriticalThreshold);
        const diff = maxV - minV;

        const co2VInd = co2 - minV;
        const minTInd = vm.silo.maxHeadSpaceCo2WarningThreshold - minV;
        const maxTInd = vm.silo.maxHeadSpaceCo2CriticalThreshold - minV;

        vm.co2ValIndicatorPct = parseFloat((Math.abs(co2VInd) / diff * 100).toFixed(2));
        vm.minTelIndicatorPct = parseFloat((Math.abs(minTInd) / diff * 100).toFixed(2));
        vm.maxTelIndicatorPct = parseFloat((Math.abs(maxTInd) / diff * 100).toFixed(2));

        if (co2 === maxV) {
            vm.co2LabelMargin = '-80%';
        } else {
            vm.co2LabelMargin = '-50%';
        }
    }

    function calculateDewpointCanvasData() {
        const minV = Math.min(0, vm.tempVal, vm.dewpVal, vm.silo.maxHeadSpaceTemperatureThreshold);
        const maxV = Math.max(vm.tempVal, vm.dewpVal, vm.silo.maxHeadSpaceTemperatureThreshold);
        const diff = maxV - minV;

        const tempInd = vm.tempVal - minV;
        const dewpInd = vm.dewpVal - minV;
        const maxTInd = vm.silo.maxHeadSpaceTemperatureThreshold - minV;

        vm.tempIndicatorPct = parseFloat((Math.abs(tempInd) / diff * 100).toFixed(2));
        vm.dewpIndicatorPct = parseFloat((Math.abs(dewpInd) / diff * 100).toFixed(2));
        vm.maxTHSIndicatorPct = parseFloat((Math.abs(maxTInd) / diff * 100).toFixed(2));
        vm.dewpDiffConnectorPct = Math.min(vm.tempIndicatorPct, vm.dewpIndicatorPct);
        vm.dewpDiffConnectorWidthPct = Math.abs(vm.tempIndicatorPct - vm.dewpIndicatorPct);
    }

    function loadSiloInformation() {
        vm.silo = null;
        vm.headSpaceSensor = null;
        reset();

        if (vm.siloId) {
            vm.loading = true;
            siloService.getSilo(vm.siloId).then(
                function success(silo) {
                    vm.silo = silo;
                    calculateCo2CanvasData();
                    calculateDewpointCanvasData();

                    siloService.getSiloHeadSpaceSensor(vm.siloId).then(
                        (headSpaceSensor) => {
                            vm.headSpaceSensor = headSpaceSensor[0];
                            if (vm.headSpaceSensor) {
                                var latestValuesSubscriptionsInfo = [{
                                    type: types.datasourceType.entity,
                                    entityType: types.entityType.device,
                                    entityId: vm.headSpaceSensor.id.id,
                                    entityName: vm.headSpaceSensor.name,
                                    timeseries: [
                                        {name: vm.agTypes.telemetry.headSpaceSensor.temperature},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.humidity},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.dewpoint},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.co2level},
                                        {name: vm.agTypes.telemetry.headSpaceSensor.pressure}
                                    ]
                                }];
                                vm.latestValuesSubscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
                                vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
                                vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.limit = 1;
                                vm.ctx.subscriptionApi.createSubscriptionFromInfo (
                                    types.widgetType.timeseries.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
                                    (subscription) => {
                                        vm.latestValuesSubscription = subscription;
                                    }
                                );

                                vm.headSpaceCo2LevelSubscriptionsInfo = angular.copy(latestValuesSubscriptionsInfo);
                                vm.headSpaceCo2LevelSubscriptionsInfo[0].timeseries = [
                                    {
                                        name: agTypes.telemetry.headSpaceSensor.co2level,
                                        label: $translate.instant('ag.silo.head-space-co2level'),
                                        color: '#4339F2',
                                    }
                                ];
                            }
                        }
                    );

                    vm.loading = false;
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    }

    function getNumberValue(keyData){
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

    function resolveCo2Color() {
        let color = agTypes.colors.normalGreyColor;
        if (vm.silo) {
            if (vm.co2Val >= vm.silo.maxHeadSpaceCo2WarningThreshold) {
                color = agTypes.colors.maxColor;
            }
            if (vm.co2Val >= vm.silo.maxHeadSpaceCo2CriticalThreshold) {
                color = agTypes.colors.errorColor;
            }
        }

        return color;
    }

    function getFormattedTs(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][0])) {
            if (!isNaN(keyData.data[0][0])) {
                var ts = Number(keyData.data[0][0]);
                return $filter('date')(ts, 'yyyy-MM-dd HH:mm:ss');
            } else {
                return '';
            }
        } else {
            return '';
        }
    }

    function resolveDewDiffColor() {
        let color = agTypes.colors.normalGreyColor;
        if (vm.silo) {
            if (vm.diff <= vm.silo.minHeadSpaceDewpointTemperatureDiffThreshold) {
                color = agTypes.colors.errorColor;
            }
        }

        return color;
    }

    function displayValueTooltip($event, value, label, timestamp, color) {
        displayTelemetryTooltip($event, $scope, $compile, value, label, timestamp, color);
    }
}
